import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';
//import { authApi } from '@/services/api/auth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
//import toast from 'react-hot-toast';

// Validation schema
const loginSchema = z.object({
    email: z
        .string()
        .min(1, 'El email es requerido')
        .email('Ingresa un email válido'),
    password: z
        .string()
        .min(1, 'La contraseña es requerida'),
    rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get('redirect') || '/';

    const { login, isLoading } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setError,
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            rememberMe: false,
        },
    });

    const onSubmit = async (data: LoginFormData) => {
        try {
            await login({
                email: data.email,
                password: data.password,
            });
            //toast.success('¡Bienvenido de vuelta!');
            navigate(redirectTo, { replace: true });
        } catch (error: any) {
            //const message = error?.response?.data?.message || 'Error al iniciar sesión';
            // Handle specific validation errors
            if (error?.response?.data?.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field as keyof LoginFormData, {
                        message: serverErrors[field][0],
                    });
                });
            } else if (error?.response?.status === 401) {
                setError('password', {
                    message: 'Email o contraseña incorrectos',
                });
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-xl p-8"
                >
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-xl">T</span>
                            </div>
                            <span className="text-2xl font-bold text-gray-900">Eisgemi</span>
                        </Link>

                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            Iniciar sesión
                        </h2>
                        <p className="text-gray-600">
                            Accede a tu cuenta y disfruta de las mejores ofertas
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <Input
                            {...register('email')}
                            type="email"
                            label="Email"
                            placeholder="tu@email.com"
                            leftIcon={<Mail className="w-5 h-5" />}
                            error={errors.email?.message}
                            fullWidth
                        />

                        <div className="relative">
                            <Input
                                {...register('password')}
                                type={showPassword ? 'text' : 'password'}
                                label="Contraseña"
                                placeholder="Tu contraseña"
                                leftIcon={<Lock className="w-5 h-5" />}
                                error={errors.password?.message}
                                fullWidth
                            />

                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                            </button>
                        </div>

                        {/* Remember me and forgot password */}
                        <div className="flex items-center justify-between">
                            <label className="flex items-center">
                                <input
                                    {...register('rememberMe')}
                                    type="checkbox"
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-600">Recordarme</span>
                            </label>

                            <Link
                                to="/forgot-password"
                                className="text-sm text-primary-600 hover:text-primary-500"
                            >
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            loading={isLoading || isSubmitting}
                            fullWidth
                            size="lg"
                            rightIcon={<LogIn className="w-5 h-5" />}
                        >
                            Iniciar sesión
                        </Button>
                    </form>

                    {/* Social Login (Future feature) */}
                    <div className="mt-6">
                        {/* <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">O continúa con</span>
                            </div>
                        </div> */}

                        {/* <div className="mt-6 grid grid-cols-2 gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    //toast.info('Próximamente disponible')
                                    toast('próximamente disponible', {
                                        icon: 'ℹ️',
                                        style: {
                                            background: '#007bff',
                                            color: '#fff',
                                        },
                                    });
                                }}
                                className="w-full"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                Google
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    //toast.info('Próximamente disponible')
                                    toast('próximamente disponible', {
                                        icon: 'ℹ️',
                                        style: {
                                            background: '#007bff',
                                            color: '#fff',
                                        },
                                    });
                                }}
                                className="w-full"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                                Facebook1
                            </Button>
                        </div> */}
                    </div>

                    {/* Footer */}
                    <div className="text-center mt-6">
                        <p className="text-sm text-gray-600">
                            ¿No tienes una cuenta?{' '}
                            <Link
                                to={`/register${redirectTo !== '/' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`}
                                className="text-primary-600 hover:text-primary-500 font-medium"
                            >
                                Regístrate aquí
                            </Link>
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;